import { Box } from '@mui/material';
import { useState } from 'react';
import { useIntl } from 'react-intl';

import { useFeatureFlagSplit } from '@verantos-dev/app-state-base';
import { useAuth } from '@verantos-dev/core-auth';
import {
  DeleteAnalyticalVariableDialog,
  RenameAnalyticalVariableDialog,
} from '@verantos-dev/feature-analytical-variables';
import { ActionMenu, type ActionMenuItemProps } from '@verantos-dev/shared-components';

import type { ProjectArtifact } from '../types';

interface VariableRowActionsProps {
  artifact: ProjectArtifact;
  projectId: string;
}

/**
 * Per-row action menu for analytical-variable rows on the project artifact
 * list. Creator-only, mirroring ``AnalyticalDatasetRowActions``'s shape
 * (self-contained menu + own dialog state, reusing the dialogs the variable
 * library page uses).
 *
 * - **Rename** (OR-6090) — no flag; the creator always sees it.
 * - **Delete** (OR-6234) behind ``research_assistant_variable_delete``. Offered
 *   whatever the status chip says: a Draft row may be a never-saved draft, a
 *   saved variable with an edit open, or a saved variable that is simply not
 *   verified, and the server resolves which. Withheld only for rows this
 *   project merely references (``relationship === 'referenced'``) and for
 *   knowledge rows, which are not library records.
 *   TODO: cleanup with research_assistant_variable_delete ff
 *
 * Gates on ``artifact.kindSpecific.createdByUserId`` — the variable's actual
 * creator — NOT ``artifact.owner.id``, which is who attached the variable to
 * *this* project (a different person, in general).
 */
export const VariableRowActions = ({ artifact, projectId }: VariableRowActionsProps) => {
  const intl = useIntl();
  const {
    user: { userId: currentUserId },
  } = useAuth();
  const isDeleteEnabled = useFeatureFlagSplit(
    'research_assistant_variable_delete',
    false
  ) as boolean;
  const [isRenameOpen, setIsRenameOpen] = useState(false);
  const [isDeleteOpen, setIsDeleteOpen] = useState(false);

  const creatorUserId = (artifact.kindSpecific?.createdByUserId as string | null) ?? null;
  const isCreator = creatorUserId !== null && creatorUserId === currentUserId;
  const isKnowledgeRow = artifact.kindSpecific?.source === 'knowledge';
  const isReferenced = artifact.relationship === 'referenced';

  const showRename = isCreator;
  const showDelete = isCreator && isDeleteEnabled && !isKnowledgeRow && !isReferenced;

  if (!showRename && !showDelete) return null;

  const displayName = artifact.displayName ?? artifact.name;
  const menu: ActionMenuItemProps[] = [];
  if (showRename) {
    menu.push({
      id: `variableArtifactActionMenu-rename-${artifact.id}`,
      label: intl.formatMessage({ id: 'projects.analyticalVariables.actionMenu.rename.label' }),
      onClick: () => setIsRenameOpen(true),
      icon: 'rename',
    });
  }
  if (showDelete) {
    menu.push({
      id: `variableArtifactActionMenu-delete-${artifact.id}`,
      label: intl.formatMessage({ id: 'projects.analyticalVariables.actionMenu.delete.label' }),
      onClick: () => setIsDeleteOpen(true),
      icon: 'delete',
    });
  }

  return (
    <Box data-testid={`variable-row-actions-${artifact.id}`} sx={{ display: 'inline-flex' }}>
      <ActionMenu
        id={`variableArtifactActionMenu-${artifact.id}`}
        menuItems={menu}
        stopClickEventPropagation
      />
      {isRenameOpen && (
        <RenameAnalyticalVariableDialog
          analyticalVariableId={artifact.id}
          currentName={displayName}
          onClose={() => setIsRenameOpen(false)}
          projectId={projectId}
        />
      )}
      {isDeleteOpen && (
        <DeleteAnalyticalVariableDialog
          analyticalVariableId={artifact.id}
          variableName={displayName}
          onClose={() => setIsDeleteOpen(false)}
          projectId={projectId}
        />
      )}
    </Box>
  );
};
